// ---------------------------------------------------------------------------
// AppLock — PBKDF2 key derivation via Web Crypto API
// ---------------------------------------------------------------------------
const ENCODER = new TextEncoder();
/**
 * Import a raw password string as a CryptoKey suitable for PBKDF2 derivation.
 */
async function importPassword(password) {
    return crypto.subtle.importKey('raw', ENCODER.encode(password), 'PBKDF2', false, ['deriveBits', 'deriveKey']);
}
/**
 * Build the context-specific salt.
 *
 * We append a single byte to separate the encryption key derivation
 * from the verification hash derivation, ensuring that the stored
 * verification hash cannot be used as (or to derive) the encryption key.
 *
 * - `0x01` → encryption key
 * - `0x02` → verification hash
 */
function contextSalt(salt, context) {
    const suffix = context === 'encrypt' ? 0x01 : 0x02;
    const extended = new Uint8Array(salt.length + 1);
    extended.set(salt);
    extended[salt.length] = suffix;
    return extended;
}
/**
 * Derive a non-extractable AES-256-GCM CryptoKey from a password.
 * Used for encrypting / decrypting stored data (Tier 2).
 */
export async function deriveEncryptionKey(password, salt, iterations) {
    const baseKey = await importPassword(password);
    return crypto.subtle.deriveKey({
        name: 'PBKDF2',
        salt: contextSalt(salt, 'encrypt'),
        iterations,
        hash: 'SHA-256',
    }, baseKey, { name: 'AES-GCM', length: 256 }, false, // non-extractable — cannot be exported from the JS runtime
    ['encrypt', 'decrypt']);
}
/**
 * Derive a verification hash from a password.
 * This hash is stored (alongside the salt) to verify future unlock attempts.
 * It uses a different derivation context than the encryption key.
 */
export async function deriveVerificationHash(password, salt, iterations) {
    const baseKey = await importPassword(password);
    return crypto.subtle.deriveBits({
        name: 'PBKDF2',
        salt: contextSalt(salt, 'verify'),
        iterations,
        hash: 'SHA-256',
    }, baseKey, 256 // 32 bytes
    );
}
/**
 * Generate a cryptographically random salt.
 */
export function generateSalt(length = 16) {
    return crypto.getRandomValues(new Uint8Array(length));
}
// ---------------------------------------------------------------------------
// Encoding helpers (base64 <-> Uint8Array)
// ---------------------------------------------------------------------------
/** Encode bytes to base64. */
export function toBase64(bytes) {
    // Handle both Uint8Array and ArrayBuffer (including cross-realm ArrayBuffer
    // from Node's webcrypto which may fail instanceof checks)
    const arr = bytes instanceof Uint8Array
        ? bytes
        : new Uint8Array(bytes);
    let binary = '';
    for (let i = 0; i < arr.length; i++) {
        binary += String.fromCharCode(arr[i]);
    }
    return btoa(binary);
}
/** Decode URL-safe base64 to Uint8Array. */
export function fromBase64(b64) {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}
/**
 * Constant-time comparison of two ArrayBuffers.
 * Prevents timing attacks on hash verification.
 */
export function timingSafeEqual(a, b) {
    if (a.byteLength !== b.byteLength)
        return false;
    const viewA = new Uint8Array(a);
    const viewB = new Uint8Array(b);
    let diff = 0;
    for (let i = 0; i < viewA.length; i++) {
        diff |= viewA[i] ^ viewB[i];
    }
    return diff === 0;
}
//# sourceMappingURL=key-derivation.js.map